import {
  Badge,
  Box,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import IconSaveButton from '../../../../../components/Button/IconSaveButton';
import IconCancelButton from '../../../../../components/Button/IconCancelButton';
import {useCallback, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {companyIdSelector} from '../../../../../store/Slices/companySlice';
import {useSelector} from 'react-redux';
import {apiPut} from '../../../../../axiosConfig';
import {UserCanAny} from '../../../../../components/UserCan';
import IconEditButton from '../../../../../components/Button/IconEditButton';
import {IStation} from '../../../../../dto/Station.dto';

type props = {
  stationId: number;
  isStingerIntervalEdit: boolean;
  setIsStingerIntervalEdit: (val: boolean) => void;
  stingerInterval: number;
  setStingerInterval: (val: number) => void;
  setSelectedStation: React.Dispatch<React.SetStateAction<IStation | null>>;
  handleCancel: () => void;
};
const StationStingerPlayCount = ({
  stationId,
  isStingerIntervalEdit,
  setIsStingerIntervalEdit,
  stingerInterval,
  setStingerInterval,
  setSelectedStation,
  handleCancel,
}: props) => {
  return (
    <Box className='flex items-center'>
      <Typography className='mr-5'>Stinger play count </Typography>
      {!isStingerIntervalEdit ? (
        <Stack direction='row' spacing={1}>
          <Badge
            badgeContent={stingerInterval}
            showZero
            color='primary'
            className='mt-4 mr-4'
          />
          <UserCanAny scope='is-admin,station:update'>
            <IconEditButton
              aria-label='edit'
              onClick={() => setIsStingerIntervalEdit(true)}
            />
          </UserCanAny>
        </Stack>
      ) : (
        <EditStingerIntervalCmpt
          stationId={stationId}
          setIsStingerIntervalEdit={setIsStingerIntervalEdit}
          stingerInterval={stingerInterval}
          setStingerInterval={setStingerInterval}
          setSelectedStation={setSelectedStation}
          handleCancel={handleCancel}
        />
      )}
    </Box>
  );
};

export default StationStingerPlayCount;

export const EditStingerIntervalCmpt = ({
  stationId,
  setIsStingerIntervalEdit,
  stingerInterval,
  setStingerInterval,
  setSelectedStation,
  handleCancel,
}: {
  stationId: number;
  setIsStingerIntervalEdit: (val: boolean) => void;
  stingerInterval: number;
  setStingerInterval: (val: number) => void;
  setSelectedStation: React.Dispatch<React.SetStateAction<IStation | null>>;
  handleCancel: () => void;
}) => {
  const companyId = useSelector(companyIdSelector);
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const handleTextChange = useCallback(
    (val: number) => {
      if (val >= 0) {
        setStingerInterval(val);
      }
    },
    [setStingerInterval],
  );

  const handleSubmit = useCallback(() => {
    setIsLoading(true);
    let payload = {preset_stinger_interval: stingerInterval};
    apiPut(`/v1/company/${companyId}/station/${stationId}`, payload)
      .then(() => {
        setSelectedStation(prev => {
          if (prev) {
            return {...prev, preset_stinger_interval: stingerInterval};
          }
          return prev;
        });
        setIsStingerIntervalEdit(false);
        setIsLoading(false);
        queryClient.invalidateQueries({
          queryKey: ['api/v1/company', Number(companyId), 'stations'],
          refetchType: 'active',
        });
      })
      .catch(_error => {
        setIsLoading(false);
      });
  }, [
    companyId,
    queryClient,
    setIsStingerIntervalEdit,
    setSelectedStation,
    stationId,
    stingerInterval,
  ]);

  if (isLoading) {
    return (
      <Stack direction='row' spacing={2} alignItems='center'>
        <Skeleton variant='rectangular' className='w-60 h-40 ml-1 radius-5' />
      </Stack>
    );
  }

  return (
    <Stack direction='row' spacing={2} alignItems={'baseline'}>
      <TextField
        variant='outlined'
        type='number'
        value={stingerInterval ?? ''}
        className='w-70'
        onChange={e => handleTextChange(Number(e.target.value))}
      />

      <Box className='pt-1'>
        <IconSaveButton
          disabled={stingerInterval < 0}
          aria-label='complete'
          onClick={handleSubmit}
        />
      </Box>
      <Box className='pt-1'>
        <IconCancelButton title='cancel' onClick={handleCancel} />
      </Box>
    </Stack>
  );
};
